import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePoste } from "@hooks/settings";
import useEmailEmployeDuplicationCheck from "@hooks/useEmailEmployeDuplicationCheck";
import { useSubmitEmployeModalForm } from "@hooks/useSubmitEmployeModalForm";
import { useForm, Controller } from "react-hook-form";
import ModalIndex from "@Modals/layout/index";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import FormSimple from "@components/formGroup/formSimple";
import InputWithAddOptions from "@components/input/InputWithAddOptions";
import CheckboxSimple from "@components/tailwindComponents/CheckboxSimple";
import ListboxEmploye from "@components/tailwindComponents/ListboxEmploye";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";
import { REGEX_EMAIL } from "@helpers/_constants";
import { useReduxSelector } from "@hooks/useReduxSelector";
import ListBoxDocuments from "@components/tailwindComponents/ListBoxDocuments";
import useGenderType from "@pages/shared/employeFormNew/PersonnalInformations/hooksGenderType";
import useEmployeeCreationAccess from "@hooks/useEmployeeCreationAccess";

export default function ModalAddEmploye({
  show,
  onClickClose,
  candidat,
  refreshApplicationList
}) {
  const employers = useReduxSelector(
    ({ commonData }) => commonData.employers
  );
  const emplacements = useReduxSelector(
    ({ commonData }) => commonData.emplacements
  );
  const permissions = useReduxSelector(
    ({ commonData }) => commonData.permissions
  );
  const postes = useReduxSelector(
    ({ commonData }) => commonData.postes
  );
  const defaultFolders = useReduxSelector(
      (state) => state.enterpriseEmployeData?.defaultFolders?.list || []
    );
  const [selectedNode, setNodeSelected] = useState({});
  const [isNodeValid, setIsNodeValid] = useState(false);
  const { _insertPoste, _editPoste, _deletePoste } = usePoste();
  const { control, handleSubmit, reset, watch, setValue, getFieldState } = useForm();
  const creationAccess = useEmployeeCreationAccess({ show, setValue, getFieldState });
  const [localState, setLocalState] = useState({
    transfertNotes: false,
    transfertDocs: false
  });
  const { t } = useTranslation("pages.employés.infoPers");
  const { isPersonalEmailValid } = useEmailEmployeDuplicationCheck();
  const { allGenderTypes: translaGenre } = useGenderType();

  useEffect(() => {
    if (show) {
      setLocalState({ transfertNotes: false, transfertDocs: false });
      reset({
        legalName: candidat.name,
        legalFirstName: candidat.firstname,
        personalEmail: candidat.email,
        legalGender: translaGenre.find(item => item._id === candidat?.gender)
      });
    }
  }, [show, translaGenre, candidat]);
  
  useEffect(() => {
    if (!localState.transfertDocs) {
      setNodeSelected({});
      //setValue("targetFolder", null);
    }
  }, [localState.transfertDocs]);

  const toggleTransfertNotes = useCallback(
    () =>
      setLocalState((prevState) => ({
        ...prevState,
        transfertNotes: !prevState.transfertNotes
      })),
    []
  );
  const toggleTransfertDocs = useCallback(
    () =>
      setLocalState((prevState) => ({
        ...prevState,
        transfertDocs: !prevState.transfertDocs
      })),
    []
  );
  const [isSaving, setIsSaving] = useState(false);
  const {
    saveFormData,
    showErrorEmploye,
    isFormError,
    showError
  } = useSubmitEmployeModalForm();

  const setLastCreatedEmployeIdAndCreatedBy = (employeId, createdBy) => {
    localStorage.setItem(
      "lastEmployeIdAndCreatedBy",
      JSON.stringify({
        employeId,
        createdBy
      })
    );
  };

  const onSubmit = async (data) => {
    if (isSaving) return;
    setIsSaving(true)
    if (!candidat) {
      showErrorEmploye();
      setIsSaving(false)
      return false;
    }

    const isEmailValid = await isPersonalEmailValid(data.personalEmail);
    if (!isEmailValid) {
      setIsSaving(false);
      return false;
    }
    if ((!(selectedNode?._id || selectedNode?.id) && !selectedNode?.value ) &&  localState.transfertDocs) {
        setIsNodeValid(true);
        setIsSaving(false);
        showError("Veuillez sélectionner un dossier de destination");
        return;
    }
    setIsNodeValid(false);
    saveFormData({
      data,
      close: onClickClose,
      dataCandidat: {
        ...candidat,
        id: candidat._id,
        shareAlldocs: localState.transfertDocs,
        shareAllNotes: localState.transfertNotes,
        selectedNode
      },
      setLastCreatedEmployeIdAndCreatedBy,
      callback: () => {
        setIsSaving(false)
        refreshApplicationList();
      }
    });
  };

   const handleChangeNode = (node)=> {
    setNodeSelected({...node})
    setIsNodeValid(false)
    //setValue("targetFolder", null);
  }
 
  return (
    <ModalIndex
      isOpen={show}
      onClickClose={onClickClose}
      customTitle={
        <h3 className="arh-text-2xl arh-font-poppins arh-font-semibold arh-text-darkColor ">
          {t("Création dossier employé")}
          <div className="arh-text-xs arh-font-poppins arh-font-semibold arh-text-darkColor arh-mt-2">
            <div className="arh-mb-1">
              {t("La fiche du candidat sera convertie en dossier employé")}.
            </div>
            <div>
              {t("Vous pourrez également transférer ses notes et fichiers")}
            </div>
          </div>
        </h3>
      }
      buttonOptions={{
        click: handleSubmit(onSubmit, isFormError),
        color: "green",
        title: t("Créer le dossier")
      }}
      buttonCancelOptions={{
        click: onClickClose,
        color: "outlined",
        title: t("Annuler")
      }}
      widthType="very-large"
      isLoading={isSaving || creationAccess.isLoading}
    >
      <div className="arh-flex arh-flex-wrap arh-relative ">
        <div className="arh-w-full arh-flex arh-justify-between arh-space-x-8">
          <div className="arh-w-1/3 arh-mb-[0.938rem]">
            <Controller
              control={control}
              name="legalName"
              rules={{ required: true }}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { invalid }
              }) => (
                <FormSimple
                  label={`${t("Nom")} *`}
                  placeholder={t("Nom")}
                  ParentForm="arh-mb-[0.938rem]"
                  classLabel="arh-flex"
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  invalid={invalid}
                />
              )}
            />
            <Controller
              control={control}
              name="emplacement"
              defaultValue={[]}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <div className="arh-mb-[0.938rem]">
                  <label
                    className={`arh-flex arh-w-full arh-font-poppins  arh-font-medium arh-text-xs arh-mb-1 ${invalid ? "arh-text-[#F60A20]" : "arh-text-black"
                      }`}
                  >
                    {`${t("Emplacement")} *`}
                  </label>
                  <div className="arh-w-full dropdown_content_hour dropdown_hoursWorking filter_posteAvailable">
                    <ListboxSimple
                      placeholder={t("Emplacement")}
                      optionList={emplacements}
                      optionTextAttribut="name"
                      optionValueAttibut="_id"
                      selectedValue={value}
                      setSelectedValue={onChange}
                      invalid={invalid}
                      multiple
                      showReset
                    />
                  </div>
                </div>
              )}
            />
            <Controller
              control={control}
              name="permission"
              defaultValue=""
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <div className="arh-mb-[0.938rem]">
                  <label
                    className={`arh-flex arh-w-full arh-font-poppins  arh-font-medium arh-text-xs arh-mb-1 ${invalid ? "arh-text-[#F60A20]" : "arh-text-black"
                      }`}
                  >
                    {`${t("Permission")} *`}
                  </label>
                  <div className="arh-w-full dropdown_content_hour dropdown_hoursWorking filter_posteAvailable">
                    <ListboxSimple
                      placeholder={t("Permission")}
                      optionList={creationAccess.permissions.length ? creationAccess.permissions : permissions}
                      optionTextAttribut="role"
                      optionValueAttibut="_id"
                      selectedValue={value}
                      setSelectedValue={onChange}
                      invalid={invalid}
                    />
                  </div>
                </div>
              )}
            />
            <Controller control={control} name="accessProfile" defaultValue={null} render={({ field: { value, onChange, name } }) => (
              <div className="arh-mb-[0.938rem]">
                <label className="arh-mb-1 arh-flex arh-font-poppins arh-text-xs">{t("Profil d'accès")}</label>
                <ListboxSimple name={name} placeholder={t("Profil d'accès")} optionList={creationAccess.accessProfiles} optionTextAttribut="title" selectedValue={value} setSelectedValue={onChange} noRadio showReset />
              </div>
            )} />
          </div>

          <div className="arh-flex arh-flex-col arh-w-1/3">
            <Controller
              control={control}
              name="legalFirstName"
              rules={{ required: true }}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { invalid }
              }) => (
                <FormSimple
                  label={`${t("Prénom")} *`}
                  placeholder={t("Prénom")}
                  ParentForm="arh-mb-[0.938rem]"
                  classLabel="arh-flex"
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  invalid={invalid}
                />
              )}
            />

            <Controller
              control={control}
              name="personalEmail"
              rules={{
                required: true,
                pattern: {
                  value: REGEX_EMAIL,
                  message: t("object_invalid", { object: "emails" })
                }
              }}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { invalid }
              }) => (
                <FormSimple
                  label={`${t("Courriel de correspondance")} *`}
                  placeholder={t("Courriel de correspondance")}
                  ParentForm="arh-mb-[0.938rem]"
                  classLabel="arh-flex"
                  onChange={(e) => onChange(e.target.value.toLowerCase())}
                  onBlur={onBlur}
                  value={value}
                  invalid={invalid}
                />
              )}
            />
            <Controller
              control={control}
              name="hierarchical"
              defaultValue=""
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <div className="arh-mb-[0.938rem]">
                  <label
                    className={`arh-flex arh-w-full arh-font-poppins  arh-font-medium arh-text-xs arh-mb-1 ${invalid ? "arh-text-[#F60A20]" : "arh-text-black"
                      }`}
                  >
                    {`${t("Supérieur hiérarchique")} *`}
                  </label>
                  <div className="arh-w-full dropdown_content_hour dropdown_hoursWorking filter_posteAvailable">
                    <ListboxEmploye
                      placeholder={t("Supérieur hiérarchique")}
                      name="hierarchical"
                      optionList={employers}
                      selectedValue={value}
                      setSelectedValue={onChange}
                      invalid={invalid}
                    />
                  </div>
                </div>
              )}
            />
          </div>

          <div className="arh-flex arh-flex-col arh-w-1/3">
            <Controller
              control={control}
              name="post"
              defaultValue=""
              rules={{ required: true }}
              render={({
                field: { onChange, value, name },
                fieldState: { invalid }
              }) => (
                <div className="arh-mb-[0.938rem]">
                  <label
                    className={`arh-flex arh-w-full arh-font-poppins  arh-font-medium arh-text-xs arh-mb-1 ${invalid ? "arh-text-[#F60A20]" : "arh-text-black"
                      }`}
                  >
                    {`${t("Poste")} *`}
                  </label>
                  <div className="arh-w-full dropdown_content_hour dropdown_hoursWorking filter_posteAvailable">
                    <InputWithAddOptions
                      defaultText={t("Poste")}
                      optionsList={postes}
                      secondDefaultText={t("Nom poste")}
                      name={name}
                      value={value}
                      optionListLabel="title"
                      onChange={onChange}
                      showAction={false}
                      addItem={_insertPoste}
                      editItem={_editPoste}
                      deleteItem={_deletePoste}
                      validateDropdown={invalid}
                      withSearch
                    />
                  </div>
                </div>
              )}
            />
            <Controller
              control={control}
              name="legalGender"
              defaultValue=""
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <div className="arh-mb-[0.938rem]">
                  <label
                    className={`arh-flex arh-w-full arh-font-poppins  arh-font-medium arh-text-xs arh-mb-1 ${invalid ? "arh-text-[#F60A20]" : "arh-text-black"
                      }`}
                  >
                    {`${t("Genre légal")} *`}
                  </label>
                  <div className="arh-w-full dropdown_content_hour dropdown_hoursWorking filter_posteAvailable">
                    <ListboxSimple
                      placeholder={t("Genre légal")}
                      optionList={translaGenre}
                      selectedValue={value}
                      setSelectedValue={onChange}
                      invalid={invalid}
                      noRadio
                      withTooltip
                      showReset
                    />
                  </div>
                </div>
              )}
            />
            <div className="arh-w-full arh-mb-[0.938rem] arh-flex arh-justify-betweeen arh-items-center">
              <div className="arh-w-full employerThemeDatePicker blackSvgColor calendar_with_btn DateRangePicker_customisation--green datepicker_vacation arh-static">
                <label className="arh-flex arh-w-full arh-font-poppins arh-text-black arh-font-medium arh-text-xs arh-mb-1">
                  {t("Date d'embauche")}
                </label>
                <Controller
                  control={control}
                  name="hiringDate"
                  defaultValue=""
                  render={({ field: { onChange, value } }) => (
                    <DefaultDatePicker
                      className="datepickerDropdown GreenThemeSelect"
                      name="deadline"
                      placeholder={t("Date d'embauche")}
                      dateValue={value}
                      setDateValue={onChange}
                      classPosition="arh-bottom-[3.2rem] arh-flex arh-justify-center"
                    />
                  )}
                />
              </div>
            </div>
            <div className="arh-w-full arh-text-left">
              <span className="arh-w-full arh-font-poppins arh-font-light arh-italic arh-text-black arh-text-xs arh-mb-1">
                {t(
                  "Date nécéssaire pour utiliser la fonctionnalité d’onboarding"
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="arh-flex arh-flex-col arh-space-y-5">
          <div className="arh-flex arh-items-start arh-py-2 arh-px-8 arh-bg-greyColor arh-rounded">
            <div className="arh-font-popins arh-font-semibold arh-text-black arh-text-xs">
              <CheckboxSimple
                name="item"
                label={t("Transférer toutes les notes")}
                checked={localState.transfertNotes}
                onClick={toggleTransfertNotes}
                haveHover={false}
                className="!arh-pl-0"
              />
            </div>
          </div>

          <div className="arh-flex arh-flex-col arh-items-start arh-py-2 arh-px-8 arh-rounded arh-bg-greyColor">
            <div className="arh-font-popins arh-font-semibold  arh-text-black arh-text-xs">
              <CheckboxSimple
                name="item"
                label={t("Transférer tous les documents")}
                checked={localState.transfertDocs}
                onClick={toggleTransfertDocs}
                haveHover={false}
                className="!arh-pl-0"
              />
            </div>
            <ListBoxDocuments
              readOnly={!Boolean(localState.transfertDocs)}
              defaultFolders={defaultFolders}
              control={control}
              watch={watch}
              setNodeSelected={handleChangeNode}
              customTranslator={t}
              selectedNode={selectedNode}
              invalid={isNodeValid}
            />
          </div>
        </div>
      </div>
    </ModalIndex  >
  );
}
